import { FC, Fragment } from "react";
import Container from "@/components/Container";
import useMediaQuery from "@/custom_hooks/mediaQuery";
import { useParams, Navigate } from "react-router-dom";
import cx from "classnames";
import { TEAM_CONTENT_VALUES } from "@/components/Team/constants";

type RowSegment = { bold: boolean; text: string };

const BOLD_TAG_PATTERN = /<b>([\s\S]*?)<\/b>/g;

const splitBoldSegments = (text: string): RowSegment[] => {
  const segments: RowSegment[] = [];
  let lastIdx = 0;
  for (const match of text.matchAll(BOLD_TAG_PATTERN)) {
    const idx = match.index ?? 0;
    if (idx > lastIdx) {
      segments.push({ bold: false, text: text.slice(lastIdx, idx) });
    }
    segments.push({ bold: true, text: match[1] });
    lastIdx = idx + match[0].length;
  }
  if (lastIdx < text.length) {
    segments.push({ bold: false, text: text.slice(lastIdx) });
  }
  return segments;
};

const parseAchievementRow = (text: string) => {
  const trimmed = text.trim();
  const dashIdx = trimmed.indexOf(" – ");
  const head = dashIdx === -1 ? trimmed : trimmed.slice(0, dashIdx);
  const description = dashIdx === -1 ? "" : trimmed.slice(dashIdx + 3).trim();

  return {
    headSegments: splitBoldSegments(head),
    descriptionSegments: description ? splitBoldSegments(description) : [],
  };
};

const TeamDetails: FC = () => {
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery(768);

  const profileContent = TEAM_CONTENT_VALUES.find((p) => p.id === Number(id));

  // If no profile content found, redirect to team list
  if (!profileContent?.content) {
    console.log("TeamDetails - no profile content found for id:", id);
    return <Navigate to="/komanda" replace />;
  }

  const quote = profileContent.content.quote;
  const hasQuote =
    quote != null &&
    (Array.isArray(quote) ? quote.length > 0 : quote.length > 0);

  return (
    <div className="flex flex-col bg-white pt-25 pb-15 md:pt-50">
      {/* Profile Section */}
      <section className="flex flex-col px-4 md:px-6">
        <Container>
          {!isMobile && (
            <div className="mx-auto flex max-w-[960px] flex-col items-center justify-center gap-5 text-center">
              <h1 className="text-ink text-[34px] leading-tight font-normal md:text-[44px]">
                {profileContent?.name}
              </h1>
              <p className="text-muted text-base leading-relaxed italic md:text-lg">
                {profileContent?.position}
              </p>
            </div>
          )}

          <div className="grid md:justify-center">
            <div className="grid md:max-w-[800px]">
              <img
                src={profileContent?.content.img_large_src}
                alt={profileContent?.alt}
                className="rounded-card aspect-[1.37/1] w-full object-cover object-top grayscale md:mt-10 md:aspect-auto md:h-[600px]"
              />

              {isMobile && (
                <div className="mt-8 flex flex-col items-center justify-center gap-2 text-center">
                  <h3 className="text-ink text-[28px] leading-tight font-normal">
                    {profileContent?.name}
                  </h3>
                  <p className="text-muted text-base leading-relaxed italic">
                    {profileContent?.position}
                  </p>
                </div>
              )}

              <div className="mt-8 flex flex-col md:mt-12">
                {profileContent.content.label.trim() ? (
                  <h2 className="text-ink text-[24px] leading-tight font-normal md:text-[34px]">
                    {profileContent.content.label}
                  </h2>
                ) : null}
                {profileContent.content.description.map((text, idx) => (
                  <p
                    key={idx}
                    className="text-muted mt-5 text-base leading-relaxed md:text-lg"
                  >
                    {text}
                  </p>
                ))}

                {profileContent.content.achievements?.map(
                  (achievement, achievementIdx) => (
                    <div key={achievement.id}>
                      <h3
                        className={cx(
                          achievementIdx === 0 ? "mt-8 md:mt-10" : "mt-8",
                          "text-ink text-xl leading-tight font-normal md:text-[24px]",
                        )}
                      >
                        {achievement.label}
                      </h3>
                      {achievement.item.map((item, itemIdx) =>
                        item.rows.map((row, rowIdx) => {
                          const { headSegments, descriptionSegments } =
                            parseAchievementRow(row.text);
                          return (
                            <p
                              key={row.id}
                              className={cx(
                                itemIdx === 0 && rowIdx === 0
                                  ? "mt-4"
                                  : rowIdx === 0
                                    ? isMobile
                                      ? "mt-2"
                                      : "mt-4"
                                    : "",
                                "text-ink leading-relaxed md:text-lg",
                              )}
                            >
                              {headSegments.map((seg, i) =>
                                seg.bold ? (
                                  <strong key={i} className="font-semibold">
                                    {seg.text}
                                  </strong>
                                ) : (
                                  <Fragment key={i}>{seg.text}</Fragment>
                                ),
                              )}
                              {descriptionSegments.length > 0 ? (
                                <>
                                  {" – "}
                                  <span className="text-muted">
                                    {descriptionSegments.map((seg, i) =>
                                      seg.bold ? (
                                        <strong
                                          key={i}
                                          className="font-semibold"
                                        >
                                          {seg.text}
                                        </strong>
                                      ) : (
                                        <Fragment key={i}>{seg.text}</Fragment>
                                      ),
                                    )}
                                  </span>
                                </>
                              ) : null}
                            </p>
                          );
                        }),
                      )}
                    </div>
                  ),
                )}

                {hasQuote && (
                  <div className="mt-10 md:mt-12">
                    <h3 className="text-ink text-xl leading-tight font-normal md:text-[24px]">
                      Mintys apie Telomerą
                    </h3>
                    <blockquote className="border-ink mt-4 border-l-2 py-1 pl-5 md:mt-6 md:pl-7">
                      {Array.isArray(quote) ? (
                        <p className="text-muted text-base leading-relaxed italic md:text-lg">
                          „
                          {quote.map((paragraph, idx) => (
                            <Fragment key={idx}>
                              {idx > 0 ? <br /> : null}
                              {paragraph.trim()}
                            </Fragment>
                          ))}
                          “
                        </p>
                      ) : (
                        <p className="text-muted text-base leading-relaxed italic md:text-lg">
                          „{quote}“
                        </p>
                      )}
                    </blockquote>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default TeamDetails;
